import { create } from 'zustand';
import { api, setToken, clearToken } from '@/lib/api';

interface User {
  id: string;
  tenantId: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
  avatarUrl?: string;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
}

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string, companyName: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  tenant: null,
  isLoading: false,
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.post<{ data: { user: User; tenant: Tenant; token?: string } }>('/auth/login', { email, password });
      if (res.data.token) setToken(res.data.token);
      set({ user: res.data.user, tenant: res.data.tenant, isLoading: false });
    } catch (err: unknown) {
      set({ isLoading: false, error: (err as Error).message || 'Login failed' });
      throw err;
    }
  },

  signup: async (name: string, email: string, password: string, companyName: string) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.post<{ data: { user: User; tenant: Tenant; token?: string } }>('/auth/signup', {
        name,
        email,
        password,
        companyName,
      });
      if (res.data.token) setToken(res.data.token);
      set({ user: res.data.user, tenant: res.data.tenant, isLoading: false });
    } catch (err: unknown) {
      set({ isLoading: false, error: (err as Error).message || 'Signup failed' });
      throw err;
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    }
    clearToken();
    set({ user: null, tenant: null });
  },

  checkAuth: async () => {
    set({ isLoading: true });
    try {
      const res = await api.get<{ data: { user: User; tenant: Tenant } }>('/auth/me');
      set({ user: res.data.user, tenant: res.data.tenant, isLoading: false });
    } catch {
      set({ isLoading: false });
    }
  },

  clearError: () => set({ error: null }),
}));
